import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuth } from '@/lib/auth/useAuth';
import { contentQueries } from '@/lib/queries/content';

export const Route = createFileRoute('/creator/')({
  component: CreatorHome,
});

function formatHours(seconds: number): string {
  const h = seconds / 3600;
  if (h < 1) return `${Math.round(seconds / 60)} min`;
  return `${h.toFixed(1)} h`;
}

function CreatorHome() {
  const { profile, loading, user } = useAuth();
  const navigate = useNavigate();
  const content = useQuery(contentQueries.listForCreator(user?.id));
  const analytics = useQuery(contentQueries.analytics());

  useEffect(() => {
    if (loading || !profile) return;
    if (profile.creator_status === 'approved') return;
    // Non-approved creators land on application-status; an admin who isn't a
    // creator has no business on a blank creator home → send to /admin.
    void navigate({ to: profile.role === 'admin' ? '/admin' : '/creator/application-status' });
  }, [loading, profile, navigate]);

  if (profile?.creator_status !== 'approved') return null;

  const rows = content.data ?? [];
  const analyticsRows = analytics.data ?? [];
  const publishedCount = rows.filter((c) => c.status === 'published').length;
  const pendingCount = rows.filter((c) => c.status === 'pending_review').length;
  const totalReaders = analyticsRows.reduce((s, r) => s + Number(r.unique_readers ?? 0), 0);
  const totalSeconds = analyticsRows.reduce((s, r) => s + Number(r.total_seconds_spent ?? 0), 0);
  const topContent = [...analyticsRows]
    .sort((a, b) => Number(b.total_seconds_spent ?? 0) - Number(a.total_seconds_spent ?? 0))
    .slice(0, 5);

  const firstName = profile?.display_name?.split(' ')[0] ?? 'there';

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="font-serif text-3xl text-ink sm:text-4xl">Welcome back, {firstName}.</h1>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Published" value={String(publishedCount)} />
        <Stat label="Total readers" value={String(totalReaders)} />
        <Stat label="Total time" value={formatHours(totalSeconds)} />
        <Stat label="Pending review" value={String(pendingCount)} to="/creator/content" />
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-2">
        <section>
          <h2 className="font-serif text-xl text-ink">Recent activity</h2>
          <ul className="mt-4 space-y-2">
            {rows.slice(0, 10).map((c) => (
              <li key={c.id}>
                <Link
                  to="/creator/content/$contentId/edit"
                  params={{ contentId: c.id }}
                  className="flex items-center justify-between rounded-md border border-ink/10 px-4 py-3 hover:border-oxblood"
                >
                  <span className="truncate font-sans text-sm text-ink">{c.title}</span>
                  <span className="ml-3 shrink-0 font-sans text-xs uppercase tracking-[0.12em] text-ink/50">
                    {c.status.replace('_', ' ')}
                  </span>
                </Link>
              </li>
            ))}
            {rows.length === 0 && (
              <li className="rounded-md border border-ink/10 px-4 py-6 text-center text-sm text-ink/50">
                No content yet. Start with your first essay —{' '}
                <Link to="/creator/write" className="text-oxblood hover:underline">
                  Write →
                </Link>
              </li>
            )}
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-xl text-ink">Top-performing</h2>
          <ul className="mt-4 space-y-2">
            {topContent.map((r) => (
              <li key={r.content_id} className="rounded-md border border-ink/10 px-4 py-3">
                <p className="truncate font-sans text-sm text-ink">{r.title}</p>
                <p className="font-sans text-xs text-ink/50">
                  {r.unique_readers} readers · {formatHours(Number(r.total_seconds_spent ?? 0))}
                </p>
              </li>
            ))}
            {topContent.length === 0 && (
              <li className="rounded-md border border-ink/10 px-4 py-6 text-center text-sm text-ink/50">
                Analytics appear once your published work is read.
              </li>
            )}
          </ul>
        </section>
      </div>

      <div className="mt-12 grid gap-4 sm:grid-cols-3">
        <ActionCard to="/creator/write" title="Write a new essay" />
        <ActionCard to="/creator/upload" title="Upload a video" />
        <ActionCard to="/creator/content" title="View all content" />
      </div>
    </div>
  );
}

function Stat({ label, value, to }: { label: string; value: string; to?: string }) {
  const body = (
    <div className="rounded-lg border border-ink/10 bg-parchment p-5">
      <p className="font-sans text-xs uppercase tracking-[0.14em] text-ink/50">{label}</p>
      <p className="mt-2 font-serif text-3xl text-ink">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function ActionCard({ to, title }: { to: string; title: string }) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-ink/10 bg-parchment p-5 font-serif text-lg text-ink hover:border-oxblood hover:text-oxblood"
    >
      {title} →
    </Link>
  );
}
